<template>
  <profile-edit-section-card
    v-if="localState"
    title="Homework help"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <fieldset class="mb-24">
      <legend class="form__label pb-8">Types of help</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <ul>
        <li v-for="option in homeworkHelpTypeOptions" :key="option.value" class="mb-8">
          <poppins-checkbox
            v-model="localState.homeworkHelpType"
            :checkbox-id="option.value"
            :label-name="option.label"
            :value="option.value"
          />
        </li>
      </ul>
    </fieldset>
    <fieldset>
      <legend class="form__label pb-8">Subjects</legend>
      <MultiselectComboBox
        id="homework-help-subjects"
        :preselected-options="localState.homeworkHelpSubjects"
        :options="homeworkHelpSubjectOptions"
        label-name="Subjects"
        @selected-options="handleSubjectUpdate"
      />
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import MultiselectComboBox from '@/components/multiselect_combo_box/MultiselectComboBox.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import { homeworkHelpTypeMap } from '@/apps/provider/profile/__types__/HomeworkHelpTypes';
import { HOMEWORK_HELP_SUBJECT_OPTIONS } from '../../__types__/CareTypeFormOptions';
import type { BabysitterProfileFormState } from '../../__types__/BabysitterProfileTypes';

export default {
  name: 'HomeworkHelpForm',
  components: {
    ProfileEditSectionCard,
    PoppinsCheckbox,
    MultiselectComboBox,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
    };
  },
  computed: {
    homeworkHelpTypeOptions: () =>
      Object.entries(homeworkHelpTypeMap).map(([value, { text }]) => ({
        value,
        label: text,
      })),
    homeworkHelpSubjectOptions: () =>
      HOMEWORK_HELP_SUBJECT_OPTIONS.map(({ value, label }) => ({
        value,
        display: label,
      })),
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as BabysitterProfileFormState | null;
      if (!state) return;

      this.localState = {
        homeworkHelpType: Array.isArray(state.homeworkHelpType) ? [...state.homeworkHelpType] : [],
        homeworkHelpSubjects: [...(state.homeworkHelpSubjects || [])],
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    handleSubjectUpdate(subjects) {
      this.localState.homeworkHelpSubjects = subjects.map((subject) => subject.value);
    },
    async handleSave() {
      if (!this.localState) return;

      if (this.hasUnsavedChanges) {
        const homeworkHelpType = [...this.localState.homeworkHelpType];
        const homeworkHelpSubjects = [...this.localState.homeworkHelpSubjects];
        await this.saveSection({
          homeworkHelp: homeworkHelpType.length > 0 || homeworkHelpSubjects.length > 0,
          homeworkHelpType,
          homeworkHelpSubjects,
        });
      }
    },
  },
};
</script>
